import React from 'react';
import PropTypes from 'prop-types';

import styles from './BookingSummaryItem.module.scss';
import { convertStringsOfDateAndTimeToDate, convertToLocaleAndFormat } from '../../../utils/time-helpers';

import IconCheck from '../../../assets/icons/icon_check.svg';
import mixpanelHelper from '../../../utils/mixpanel-helper';

const BookingSummaryItem = ({
  active,
  alternativeSelections,
  borderColor,
  cancel,
  disabled,
  done,
  finalTimestamp,
  introClass,
  lesson,
  onlyOneItem,
  removeNewScheduleFromFinalList,
  setNewActiveAppointment,
  timezone
}) => {
  const handleReschedulingRemoval = (selectedLesson) => {
    removeNewScheduleFromFinalList(selectedLesson);
    mixpanelHelper.track('Removed Selected Booking');
  };

  const getBorderColor = () => {
    if (finalTimestamp && alternativeSelections && alternativeSelections.has(finalTimestamp)) {
      return 'soft-orange'; // Alternative selection
    }
    if (lesson.alternativeCoach) {
      return 'soft-orange'; // Alternative coach selection
    }
    return borderColor || 'mint'; // Perfect match or default
  };

  const getWarningMessage = () => {
    if (finalTimestamp && alternativeSelections && alternativeSelections.has(finalTimestamp)) {
      return 'Scheduled at a different time';
    }
    if (lesson.alternativeCoach) {
      const coachName =
        lesson.alternativeCoach.first_name && lesson.alternativeCoach.last_name
          ? `${lesson.alternativeCoach.first_name} ${lesson.alternativeCoach.last_name}`
          : lesson.alternativeCoach.name || 'Alternative Coach';
      return `Session will be with coach ${coachName}`;
    }
    return null;
  };

  if (cancel && introClass)
    return (
      <li className={[styles.lessonItem, styles.cancelLessonItem].join(' ')} key={lesson.id}>
        <div className={styles.cancelLessonInfo}>
          <span className={styles.lessonNumber}>Intro Class:</span>
          <span className={styles.lessonDate} id="dont-translate">
            {lesson?.date &&
              convertToLocaleAndFormat(convertStringsOfDateAndTimeToDate(lesson?.date, lesson?.start_time), timezone, 'EEE do MMM yyyy, hh:mm aa')}
          </span>
        </div>
      </li>
    );

  if (cancel) {
    return (
      <li className={[styles.lessonItem, styles.cancelLessonItem].join(' ')} key={lesson.id}>
        <div className={styles.cancelLessonInfo}>
          {Boolean(lesson?.number) && (
            <span className={styles.lessonNumber}>
              Lesson <span id="dont-translate">{lesson.number}</span>:
            </span>
          )}
          <span className={styles.lessonDate} id="dont-translate">
            {lesson.appointment_object &&
              convertToLocaleAndFormat(
                convertStringsOfDateAndTimeToDate(lesson.appointment_object.date, lesson.appointment_object.start_time),
                timezone,
                'EEE do MMM yyyy, hh:mm aa'
              )}
          </span>
        </div>
        {!onlyOneItem && (
          <div className={styles.imageWrapper}>
            <button className={styles.cancelButton} onClick={() => handleReschedulingRemoval(lesson)}>
              Remove
            </button>
          </div>
        )}
      </li>
    );
  }

  const whatTimestampToShowForIntroSession = () => {
    if (finalTimestamp) return convertToLocaleAndFormat(new Date(finalTimestamp), timezone, 'EEE do MMM yyyy, hh:mm aa');
    return convertToLocaleAndFormat(convertStringsOfDateAndTimeToDate(lesson?.date, lesson?.start_time), timezone, 'EEE do MMM yyyy, hh:mm aa');
  };

  if (introClass) {
    return (
      <div className={[active && styles.active, disabled && styles.disabled, styles.item].join(' ')} disabled={disabled}>
        <li className={[styles.lessonItem, styles[borderColor]].join(' ')}>
          <span className={styles.lessonNumber}>MYC Session</span>
          <span className={styles.lessonDate} id="dont-translate">
            {whatTimestampToShowForIntroSession()}
          </span>
        </li>
      </div>
    );
  }

  const whatTimestampToShow = () => {
    if (finalTimestamp) return convertToLocaleAndFormat(new Date(finalTimestamp), timezone, 'EEE do MMM yyyy, hh:mm aa');
    return convertToLocaleAndFormat(
      convertStringsOfDateAndTimeToDate(lesson?.appointment_object?.date, lesson?.appointment_object?.start_time),
      timezone,
      'EEE do MMM yyyy, hh:mm aa'
    );
  };

  const whatButtonToShow = () => {
    if (done) {
      return (
        <button className={styles.checkButton} onClick={() => handleReschedulingRemoval(lesson)}>
          <img src={IconCheck} alt="remove from final list" />
        </button>
      );
    }
    return null;
  };

  return (
    <div
      className={[active && styles.active, disabled && styles.disabled, done && styles.done, styles.item].join(' ')}
      disabled={disabled}
      role="button"
      onClick={!disabled ? setNewActiveAppointment : undefined}>
      <li className={[styles.lessonItem, styles[getBorderColor()]].join(' ')} key={lesson.id}>
        <div className={styles.lessonContent}>
          <div className={styles.lessonInfo}>
            {lesson?.number ? (
              <span className={styles.lessonNumber}>
                Lesson <span id="dont-translate">{lesson.number}</span>:
              </span>
            ) : (
              <span className={styles.lessonNumber}>Convo:</span>
            )}
            <span className={styles.lessonDate} id="dont-translate">
              {whatTimestampToShow()}
            </span>
          </div>
          {getWarningMessage() && <div className={styles.warningMessage}>{getWarningMessage()}</div>}
        </div>
        {disabled ? null : <div className={styles.imageWrapper}>{whatButtonToShow()}</div>}
      </li>
    </div>
  );
};

BookingSummaryItem.propTypes = {
  active: PropTypes.bool,
  alternativeSelections: PropTypes.instanceOf(Set),
  borderColor: PropTypes.string,
  cancel: PropTypes.bool,
  disabled: PropTypes.bool,
  done: PropTypes.bool,
  finalTimestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
  introClass: PropTypes.bool,
  lesson: PropTypes.object.isRequired,
  onlyOneItem: PropTypes.bool,
  removeNewScheduleFromFinalList: PropTypes.func,
  setNewActiveAppointment: PropTypes.func,
  testId: PropTypes.string,
  timezone: PropTypes.string.isRequired
};

BookingSummaryItem.defaultProps = {
  active: false,
  borderColor: 'mint',
  cancel: false,
  disabled: false,
  done: false,
  finalTimestamp: null,
  onlyOneItem: false
};

export default BookingSummaryItem;
